/**
 * StepRockConditions.jsx
 *
 * Wizard step 5 (all paths, optional). User picks a rock-hardness band so
 * the bit-classification recommendation (D9) can highlight the right
 * grades. User can skip — `selections.rockCondition` stays null and the
 * recommendation falls back to defaults.
 *
 * PORTED from TH ToolFinder v4:
 *   tophammer-toolfinder-v4/src/components/wizard/StepRockConditions.jsx
 *   (58 lines, lines 13-56 — the colored-circle + label + MPa text grid).
 *
 * 18 May (Vandana feedback): added inline "Skip this step" button so the
 * affordance matches StepHoleDiameter's pattern. Rock conditions are
 * optional in the data model, so the wizard advances either way; the
 * button is just the visible UX cue.
 *
 * @module components/wizard/StepRockConditions
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3e)
 */

import { useWizard } from '@contexts/WizardContext';
import { ROCK_CONDITIONS } from '@data/wizardData';

export default function StepRockConditions() {
  const { selections, handleSelect, handleRockConditionsNext } = useWizard();
  const selected = selections.rockCondition;

  const handleSkip = () => {
    handleSelect('rockCondition', null);
    handleRockConditionsNext();
  };

  return (
    <section
      aria-labelledby="rock-conditions-heading"
      className="max-w-3xl mx-auto"
    >
      <header className="mb-8 text-center">
        <h1
          id="rock-conditions-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black mb-2"
        >
          Select rock conditions
        </h1>
        <p className="font-sandvik-text text-n6">
          This helps us recommend the best bit classification (optional)
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Rock conditions"
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {ROCK_CONDITIONS.map((rock) => {
          const isSelected = selected === rock.id;
          return (
            <button
              key={rock.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              data-testid={`rock-card-${rock.id}`}
              onClick={() => handleSelect('rockCondition', rock.id)}
              className={[
                'p-6 border-2 transition-all text-center bg-white',
                'focus:outline-none focus:ring-2 focus:ring-electric-blue',
                isSelected
                  ? 'border-electric-blue bg-eb-1/20'
                  : 'border-n2 hover:border-n4',
              ].join(' ')}
            >
              <div
                data-testid={`rock-circle-${rock.id}`}
                className="w-8 h-8 rounded-full mx-auto mb-3"
                style={{ backgroundColor: rock.color }}
                aria-hidden="true"
              />
              <span className="font-sandvik-text font-normal block text-carbon-black">
                {rock.label}
              </span>
              <span className="font-sandvik-text text-xs text-n5 block mt-1">
                {rock.description}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={handleSkip}
          data-testid="rock-conditions-skip"
          className="px-6 py-2 border border-n3 text-n6 hover:text-carbon-black hover:border-n5 font-sandvik-text text-sm transition-colors"
        >
          Skip this step
        </button>
      </div>
    </section>
  );
}
